import { Action } from "@packages/shared-types";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { listRecurringTemplatesQueryOptions } from "@/api/financial";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { useHasPermission } from "@/hooks/useAccessControl";
import { useFarmScope } from "@/hooks/useFarmScope";
import { formatMoney } from "@/utils/financial";
import {
	FinancialHeaderActions,
	HeaderActionButton,
} from "../-components/FinancialHeaderActions";
import { OccurrencesInbox } from "./-components/OccurrencesInbox";
import { TemplateActiveBadge } from "./-components/RecurringBadges";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/financial/recurring/",
)({
	component: RecurringListPage,
});

const ALL = "all";

function RecurringListPage() {
	const { orgId } = Route.useParams();
	const { t } = useTranslation();
	const navigate = useNavigate();
	const scope = useFarmScope();
	const canManage = useHasPermission(orgId)(Action.MANAGE_FINANCE);

	const [filter, setFilter] = useState(ALL);

	const establishmentId =
		scope.kind === "single" ? scope.establishmentId : undefined;

	const queryParams = useMemo(
		() => ({
			limit: "100",
			...(filter === "active" ? { activeOnly: "true" } : {}),
			...(establishmentId ? { establishmentId } : {}),
		}),
		[filter, establishmentId],
	);

	const { data, isLoading } = useQuery(
		listRecurringTemplatesQueryOptions(orgId, queryParams),
	);
	const rows = data?.data ?? [];

	return (
		<div className="flex flex-col gap-6">
			{canManage && (
				<FinancialHeaderActions>
					<HeaderActionButton
						icon={Plus}
						label={t("financial.recurring.new")}
						shortLabel={t("financial.recurring.newShort")}
						onClick={() =>
							navigate({
								to: "/app/org/$orgId/financial/recurring/new",
								params: { orgId },
							})
						}
					/>
				</FinancialHeaderActions>
			)}

			<OccurrencesInbox
				orgId={orgId}
				canManage={canManage}
				establishmentId={establishmentId}
			/>

			<div className="flex flex-col gap-3">
				<div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
					<h2 className="text-lg font-medium">
						{t("financial.recurring.templates.title")}
					</h2>
					<Select value={filter} onValueChange={setFilter}>
						<SelectTrigger className="sm:w-44">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value={ALL}>
								{t("financial.recurring.filters.all")}
							</SelectItem>
							<SelectItem value="active">
								{t("financial.recurring.filters.activeOnly")}
							</SelectItem>
						</SelectContent>
					</Select>
				</div>

				<div className="card bg-base-100 shadow-md">
					<div className="card-body">
						{isLoading ? (
							<div className="flex justify-center py-16">
								<LoadingSpinner />
							</div>
						) : rows.length === 0 ? (
							<div className="py-12 text-center">
								<p className="font-medium">
									{t("financial.recurring.empty.title")}
								</p>
								<p className="mt-1 text-sm text-base-content/60">
									{t("financial.recurring.empty.description")}
								</p>
							</div>
						) : (
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead>
											{t("financial.recurring.columns.description")}
										</TableHead>
										<TableHead className="hidden sm:table-cell">
											{t("financial.recurring.columns.direction")}
										</TableHead>
										<TableHead className="hidden md:table-cell">
											{t("financial.recurring.columns.frequency")}
										</TableHead>
										<TableHead className="text-right">
											{t("financial.recurring.columns.amountDefault")}
										</TableHead>
										<TableHead>
											{t("financial.recurring.columns.status")}
										</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{rows.map((template) => (
										<TableRow
											key={template.id}
											className="cursor-pointer"
											onClick={() =>
												navigate({
													to: "/app/org/$orgId/financial/recurring/$templateId",
													params: { orgId, templateId: template.id },
												})
											}
										>
											<TableCell>
												<span className="font-medium">
													{template.description}
												</span>
												{template.party && (
													<span className="block text-xs text-base-content/60">
														{template.party}
													</span>
												)}
											</TableCell>
											<TableCell className="hidden sm:table-cell">
												{t(`financial.directions.${template.direction}`)}
											</TableCell>
											<TableCell className="hidden md:table-cell">
												{t(
													`financial.recurring.frequencies.${template.frequency}`,
												)}
											</TableCell>
											<TableCell className="text-right">
												{template.amountDefault
													? formatMoney(template.amountDefault)
													: t("financial.recurring.variableAmount")}
											</TableCell>
											<TableCell>
												<TemplateActiveBadge isActive={template.isActive} />
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}
